/** @jsxImportSource theme-ui */
import { Formik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { Spinner, Text, Heading, Box } from 'theme-ui';
import { toast } from 'react-toastify';
import { useGetTicketQuery, usePutTicketMutation } from '@/store/apiService';
import TicketForm, { TicketFormValues } from './TicketForm';
import { validateTicket } from './common';
import TicketComments from './TicketComments';

const EditTicket: React.FC = () => {
    const { ticketId } = useParams<{ ticketId: string }>();
    const id = Number(ticketId);
    const ticketQuery = useGetTicketQuery({ Id: id }, { skip: !Number.isFinite(id) });
    const [editTicket, editTicketState] = usePutTicketMutation();
    const navigate = useNavigate();

    if (ticketQuery.isLoading) {
        return <Spinner size={16} />;
    }

    if (!ticketQuery.data) {
        return <Text>No ticket found</Text>;
    }

    const ticket = ticketQuery.data;

    const initialValues: TicketFormValues = {
        Id: ticket.Id,
        CompanyId: '',
        Title: ticket.Title ?? '',
        Description: ticket.Description ?? '',
        CaseTypeId: ticket.CaseTypeId ?? 0,
        CaseAreaId: ticket.CaseAreaId ?? 0,
        CaseStatusId: ticket.CaseStatusId ?? 0,
    };

    return (
        <>
            <Formik
                enableReinitialize
                initialValues={initialValues}
                validate={validateTicket}
                onSubmit={(values, { setSubmitting }) => {
                    const payload = {
                        Id: ticket.Id!,
                        Title: values.Title,
                        Description: values.Description,
                        CaseTypeId: values.CaseTypeId ? Number(values.CaseTypeId) : undefined,
                        CaseAreaId: values.CaseAreaId ? Number(values.CaseAreaId) : undefined,
                        CaseStatusId: values.CaseStatusId
                            ? Number(values.CaseStatusId)
                            : undefined,
                    };
                    editTicket(payload)
                        .unwrap()
                        .then(() => {
                            navigate('/tickets');
                        })
                        .catch((err) => {
                            setSubmitting(false);
                            toast.error(err?.data ?? err?.message ?? 'Update failed', {
                                autoClose: 5000,
                            });
                        });
                }}
            >
                {(formProps) => (
                    <TicketForm
                        mode="edit"
                        additionalErrors={editTicketState.error}
                        {...formProps}
                    />
                )}
            </Formik>
            {ticket.Id != null ? (
                <Box mt="3">
                    <Heading as="h3">Comments</Heading>
                    <TicketComments ticketId={ticket.Id} initialComments={ticket.Comments ?? []} />
                </Box>
            ) : null}
        </>
    );
};

export default EditTicket;
